/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ServerFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the telephonyServers module.
----------------------------------------------------------------*/


import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import { Server, Search } from 'lucide-react';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import {
    useServerForm, useServerData, useQuickList, useSshKey, useServerSubmit,
} from './hooks';
import { TelephonyServer } from './types';
import { ServerBasicInfoBox } from './components/ServerBasicInfoBox';
import { ServerNetworkBox } from './components/ServerNetworkBox';
import { ServerSshKeyBox } from './components/ServerSshKeyBox';
import { ServerQuickListModal } from './components/ServerQuickListModal';

export default function ServerFormView() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const duplicateId = searchParams.get('duplicate');
    const resetParam = searchParams.get('_reset');
    const mode = id && id !== 'new' ? 'edit' : 'create';

    const { t } = useI18n();
    const { setCurrentPageTitle } = useNavigationStore();
    const { showInfo } = useNotifications();
    const formRef = useRef<HTMLFormElement>(null);

    useFixedActionBar(true);

    const { formData, originalData, errors: formErrors, setFormData, setOriginalData, setErrors, handleInputChange, hasChanges } = useServerForm();
    useServerData(mode, id, duplicateId, setFormData, setOriginalData, resetParam);

    const { showQuickList, setShowQuickList, quickListSearch, setQuickListSearch, filteredQuickList, handleQuickListSelect } = useQuickList(setFormData, setOriginalData);
    const { isGeneratingKey, sshKeyStatus, generatedSshKey, isCopied, sshStatusMessage, sshButtonLabel, handleGenerateSshKey, handleCopySshKey } = useSshKey(formData);
    const { isLoading, errors: submitErrors, handleSubmit } = useServerSubmit(mode, id, formData, originalData, hasChanges, setOriginalData);

    const errors = { ...formErrors, ...submitErrors };

    const handleSaveInternal = useCallback(async (): Promise<TelephonyServer | undefined> => {
        return await handleSubmit();
    }, [handleSubmit]);

    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: '/admin/system-settings/telephony-servers',
        newPath: '/admin/system-settings/telephony-servers/new',
        getEditPath: (saved) => `/admin/system-settings/telephony-servers/${saved.id}`,
        mode,
        storageKey: 'telephony-servers-save-action',
    });

    const handleCancel = useCallback(() => {
        if (mode === 'create') {
            setFormData({
                name: '', description: '', host: '', port: 22, username: '', password: '',
                eslHost: '', freeswitchPort: 8021, freeswitchPassword: 'ClueCon', timezone: 'UTC',
                location: '', monitoringEnabled: true, healthCheckInterval: 30, useVpn: false,
                bindInterface: 'auto', vpnIp: ''
            });
        } else if (originalData) {
            setFormData(originalData);
        }
    }, [mode, originalData, setFormData]);

    useEffect(() => {
        setCurrentPageTitle(null);
        return () => { setCurrentPageTitle(null); };
    }, [setCurrentPageTitle]);

    const LIST_PATH = '/admin/system-settings/telephony-servers';

    return (
        <div className="p-6">
            <ModuleFormHeader
                icon={<Server className="h-5 w-5 text-primary" />}
                title={mode === 'create' ? t('telephonyServers.createTitle') : `${t('telephonyServers.editTitle')} - ${formData.name || ''}`}
                backButtonText={t('common.list')}
                onBack={() => navigate(LIST_PATH)}
                actions={[{ icon: <Search className="h-5 w-5 text-muted-foreground" />, tooltip: t('telephonyServers.quickList'), onClick: () => setShowQuickList(true) }]}
            />

            <div className="mt-8">
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                    <ServerBasicInfoBox formData={formData} errors={errors} onInputChange={handleInputChange} />
                    <ServerNetworkBox formData={formData} errors={errors} onInputChange={handleInputChange} />
                    <ServerSshKeyBox
                        formData={formData} isGeneratingKey={isGeneratingKey} sshKeyStatus={sshKeyStatus}
                        generatedSshKey={generatedSshKey} isCopied={isCopied} sshStatusMessage={sshStatusMessage}
                        sshButtonLabel={sshButtonLabel} onGenerateSshKey={handleGenerateSshKey}
                        onCopySshKey={handleCopySshKey} setErrors={setErrors}
                    />
                </form>
            </div>

            <ServerQuickListModal
                isOpen={showQuickList}
                quickListSearch={quickListSearch}
                filteredQuickList={filteredQuickList}
                currentItemId={mode === 'edit' && !duplicateId ? id : null}
                onClose={() => { setShowQuickList(false); setQuickListSearch(''); }}
                onSearchChange={setQuickListSearch}
                onItemSelect={(server) => handleQuickListSelect(server as TelephonyServer)}
            />

            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancel}
                isEditMode={mode === 'edit'}
                saveText={isLoading ? t('common.saving') : t('common.save')}
                cancelText={t('common.cancel')}
                saveDisabled={isLoading}
                isLoading={isLoading}
                hasChanges={hasChanges}
                useSaveDropdown
                showSave
                saveDropdownStorageKey="telephony-servers-save-action"
            />
        </div>
    );
}
